import { Injectable } from '@angular/core';
import { AndroidPermissions } from '@awesome-cordova-plugins/android-permissions/ngx';
import { Camera, CameraErrorCode, CameraPermissionState, CameraPermissionType } from '@capacitor/camera';
import { PlatformService } from './platform.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class CameraService {

  constructor(
    private androidPermissions: AndroidPermissions,
    private toast: ToastService,
    private platformService: PlatformService
  ) { }

  public async ensureCameraPermission(): Promise<boolean> {
    if (this.platformService.isWeb()) {
      return true;
    }

    return this.ensurePermission('camera', 'CAMERA-PERMISSIONS-DENIED');
  }

  public async ensurePhotosPermission(): Promise<boolean> {
    if (this.platformService.isWeb()) {
      return true;
    }

    return this.ensurePermission('photos', 'PHOTOS-PERMISSIONS-DENIED', ['granted', 'limited']);
  }

  public async ensureLegacyAndroidStoragePermission(): Promise<boolean> {
    const requestPermission = this.androidPermissions.PERMISSION.WRITE_EXTERNAL_STORAGE;
    const statusHasPermission = await this.androidPermissions.checkPermission(requestPermission);

    if (!statusHasPermission.hasPermission) {
      const statusReqPermission = await this.androidPermissions.requestPermission(requestPermission);

      if (!statusReqPermission.hasPermission) {
        await this.toast.danger('INSUFFICIENT-PERMISSIONS');
        return false;
      }
    }

    return true;
  }

  public isCameraPermissionError(error: unknown): boolean {
    return (error as { code?: string })?.code === CameraErrorCode.CameraPermissionDenied;
  }

  public isGalleryPermissionError(error: unknown): boolean {
    return (error as { code?: string })?.code === CameraErrorCode.GalleryPermissionDenied;
  }

  public isUserCancelledError(error: unknown): boolean {
    const code = (error as { code?: string })?.code;
    return code === CameraErrorCode.TakePhotoCancelled || code === CameraErrorCode.ChooseMediaCancelled;
  }

  public async notifyCameraPermissionDenied(): Promise<void> {
    await this.toast.danger('CAMERA-PERMISSIONS-DENIED');
  }

  public async notifyPhotosPermissionDenied(): Promise<void> {
    await this.toast.danger('PHOTOS-PERMISSIONS-DENIED');
  }

  private async ensurePermission(
    permission: CameraPermissionType,
    messageKey: string,
    grantedStates: CameraPermissionState[] = ['granted']
  ): Promise<boolean> {
    let status = await Camera.checkPermissions();

    if (grantedStates.includes(status[permission])) {
      return true;
    }

    status = await Camera.requestPermissions({ permissions: [permission] });

    if (grantedStates.includes(status[permission])) {
      return true;
    }

    await this.toast.danger(messageKey);
    return false;
  }

  public getBlob(b64Data: string, contentType: string, sliceSize: number = 512) {
    contentType = contentType || '';
    sliceSize = sliceSize || 512;

    let byteCharacters = atob(b64Data);
    let byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      let slice = byteCharacters.slice(offset, offset + sliceSize);

      let byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      let byteArray = new Uint8Array(byteNumbers);

      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  }
}
